import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { useQuery } from 'Utilities/hooks';

import PageLoader from 'Components/transitions/PageLoader/PageLoader';
import { Content } from 'LegacySBCRA/Content/Content';

import texts from './VerifyYourIdentityPage.json';

import styles from './VerifyYourIdentityPage.module.scss';

const LocalT = new MDText(texts);

const mockBackendCall = async (id: string, signal: AbortSignal): Promise<void> => {
  if (signal.aborted || !signal.addEventListener) {
    throw new Error('Request failed');
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => resolve(), 4000);

    const handleAbort = () => {
      clearTimeout(timeout);
      reject(new Error('Request aborted'));
    };

    signal.addEventListener('abort', handleAbort);
  });
};

export const VerifyYourIdentityPage: React.FC = () => {
  const history = useHistory();
  const id = useQuery('id');
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const TIMEOUT_MS = 7000;

    const safeRedirect = () => {
      if (hasNavigatedRef.current) {
        return;
      }
      hasNavigatedRef.current = true;
      history.push('/account');
    };

    if (!id) {
      safeRedirect();
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, TIMEOUT_MS);

    const handleVerification = async () => {
      try {
        await mockBackendCall(id, controller.signal);
        clearTimeout(timeoutId);
        safeRedirect();
      } catch {
        clearTimeout(timeoutId);
        safeRedirect();
      }
    };

    handleVerification();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [id, history]);

  return (
    <Content className={styles.wrapper}>
      <PageLoader className={styles.loader} />
      <div className={styles.content}>
        <h2>{LocalT.translate('heading')}</h2>
        <p data-thc-style-override>{LocalT.translate('content')}</p>
      </div>
    </Content>
  );
};
